"use client";

import { Download, FileSpreadsheet, FileText } from "lucide-react";
import React from "react";

import type { T_ChatAttachmentPreviewResult } from "@lattice/core-lib/types/chat";
import type { T_ChatV8AttachmentRef } from "@lattice/core-lib/types/chat-v8";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";

import "../chat-v8.css";

function getFileIcon(type: T_ChatV8AttachmentRef["type"]) {
  switch (type) {
    case "CSV":
    case "SPREADSHEET":
      return <FileSpreadsheet aria-hidden size={15} />;
    default:
      return <FileText aria-hidden size={15} />;
  }
}

function formatByteSize(byteSize: number | undefined): string | null {
  if (!byteSize || byteSize <= 0) return null;
  if (byteSize < 1024) return `${byteSize} B`;
  if (byteSize < 1024 * 1024) return `${Math.round(byteSize / 1024)} KB`;
  return `${(byteSize / (1024 * 1024)).toFixed(1)} MB`;
}

export type T_UserAttachmentStripViewProps = {
  attachments: readonly T_ChatV8AttachmentRef[];
  onDownload: (objectPath: string, fileName: string | undefined) => void;
  previews: readonly T_ChatAttachmentPreviewResult[];
};

/**
 * Attachments on a sent user message: image thumbnails through the preview
 * endpoint, document chips with on-demand original download. The view is
 * pure presentation; the default export wires previews and downloads.
 */
export function UserAttachmentStripView({
  attachments,
  onDownload,
  previews,
}: T_UserAttachmentStripViewProps) {
  const imagePreviews = previews.filter(
    (preview) => preview.renderer === "image" && preview.url,
  );
  const filePreviews = previews.filter(
    (preview) => preview.renderer === "file",
  );
  const fileRefs = attachments.filter((ref) => ref.type !== "IMAGE");

  if (attachments.length === 0) return null;

  return (
    <div className="chat-v8-user-attachments">
      {imagePreviews.map((preview) => (
        <img
          alt="Attached image"
          className="chat-v8-user-attachment-thumb"
          key={preview.attachmentObjectPath}
          src={preview.url ?? undefined}
        />
      ))}
      {fileRefs.map((ref, index) => {
        const preview = filePreviews[index];
        const sizeLabel = formatByteSize(ref.byteSize);
        return (
          <button
            className="chat-v8-user-attachment-file"
            disabled={!preview}
            key={ref.attachmentCuid}
            onClick={() =>
              preview
                ? onDownload(preview.attachmentObjectPath, ref.fileName)
                : undefined
            }
            title={`Download ${ref.fileName ?? "attachment"}`}
            type="button"
          >
            {getFileIcon(ref.type)}
            <span className="chat-v8-user-attachment-file__copy">
              <span className="chat-v8-user-attachment-file__name">
                {ref.fileName ?? "Attachment"}
              </span>
              <span className="chat-v8-user-attachment-file__meta">
                {ref.type}
                {sizeLabel ? ` · ${sizeLabel}` : ""}
              </span>
            </span>
            <Download aria-hidden size={14} />
          </button>
        );
      })}
    </div>
  );
}

export function UserAttachmentStrip({
  attachments,
  itemCuid,
}: {
  attachments: readonly T_ChatV8AttachmentRef[];
  itemCuid: string;
}) {
  const utils = trpcApi.useUtils();
  const previews = trpcApi.chat_v8.getItemAttachmentPreviews.useQuery(
    { itemCuid },
    { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000 },
  );

  const download = async (objectPath: string, fileName: string | undefined) => {
    const result = await utils.chat_v8.getItemAttachmentDownloadUrl.fetch({
      attachmentObjectPath: objectPath,
      itemCuid,
    });
    const anchor = document.createElement("a");
    anchor.href = result.data.url;
    anchor.download = fileName ?? `attachment.${result.data.fileExtension}`;
    anchor.rel = "noopener";
    anchor.click();
  };

  return (
    <UserAttachmentStripView
      attachments={attachments}
      onDownload={(objectPath, fileName) => void download(objectPath, fileName)}
      previews={previews.data?.data ?? []}
    />
  );
}
